import React, { useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLeague } from "../../hooks/useLeague";
import { useToast } from "../../context/ToastContext";
import { apiPatch, apiPost } from "../../utils/apiClient";
import { getPlayersUrl, getSpareUrl, getSparesUrl } from "../../config/api";
import { Game, Player, Spare } from "../../types/models";
import EntryPanel, { EntryStep } from "./EntryPanel";
import PlayerGrid from "./PlayerGrid";
import {
  PlayerOption,
  TeamRoster,
  formatPlayersForDropdown,
  toPlayerOption,
} from "./players";
import {
  PLAYER_SEARCH_LIMIT,
  useLeaguePlayerSearch,
} from "./useLeaguePlayerSearch";

/**
 * The order the steps are asked in. Picking a substitute carries on to the
 * jersey, because the number a spare wears for this game is the thing the
 * scorekeeper most often has to correct — and a player created on the spot has
 * no number at all yet. Answering "No substitute" stops at that step instead:
 * there is nobody to give a number to.
 */
const FLOW = ["absent", "substitute", "jersey"];

interface SpareEntryProps {
  game: Game;
  subtitle: string;
  /** Players who can be marked absent, grouped by team. */
  rosters: TeamRoster[];
  editingSpare: Spare | null;
  onSaved: () => void;
  onCancel: () => void;
}

function SpareEntry({
  game,
  subtitle,
  rosters,
  editingSpare,
  onSaved,
  onCancel,
}: SpareEntryProps) {
  const auth = useAuth();
  const { currentLeague } = useLeague();
  const { showToast } = useToast();

  const [teamId, setTeamId] = useState<number | null>(
    editingSpare?.team_id ?? null,
  );
  const [absentPlayerId, setAbsentPlayerId] = useState<number | null>(
    editingSpare?.replaced_player_id ?? null,
  );
  // The substitute is kept whole, not just by id: they are found by a
  // league-wide search, so they are usually absent from the rosters above.
  const [substitute, setSubstitute] = useState<PlayerOption | null>(
    editingSpare?.spare_player_id
      ? toPlayerOption(
          editingSpare.spare_player_id,
          editingSpare.spare_player_first_name,
          editingSpare.spare_player_last_name,
          editingSpare.spare_player_jersey,
        )
      : null,
  );
  const [substituteAnswered, setSubstituteAnswered] = useState(
    editingSpare !== null,
  );
  const [jersey, setJersey] = useState(editingSpare?.jersey ?? "");
  const [activeStepKey, setActiveStepKey] = useState(FLOW[0]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Adding a substitute who is not in the league yet, without leaving the game.
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [isCreatingPlayer, setIsCreatingPlayer] = useState(false);
  const [newPlayerError, setNewPlayerError] = useState<string | null>(null);

  const search = useLeaguePlayerSearch(currentLeague, auth.token);

  const absentPlayer = useMemo(() => {
    for (const roster of rosters) {
      const found = roster.players.find(
        (player) => player.id === absentPlayerId,
      );
      if (found) {
        return found;
      }
    }
    return null;
  }, [rosters, absentPlayerId]);

  const searchResults = useMemo(
    () => formatPlayersForDropdown(search.players),
    [search.players],
  );

  const goToNextStep = (key: string) => {
    const index = FLOW.indexOf(key);
    if (index >= 0 && index < FLOW.length - 1) {
      setActiveStepKey(FLOW[index + 1]);
    }
  };

  const handleAbsentSelect = (playerId: number, selectedTeamId: number) => {
    setAbsentPlayerId(playerId);
    setTeamId(selectedTeamId);
    goToNextStep("absent");
  };

  const handleSubstituteSelect = (player: PlayerOption | null) => {
    setSubstitute(player);
    setSubstituteAnswered(true);
    // "No substitute" has no number to set, so it ends the flow here.
    if (player !== null) {
      goToNextStep("substitute");
    }
  };

  const handleCreatePlayer = () => {
    const firstName = newFirstName.trim();
    const lastName = newLastName.trim();

    if (firstName === "" || lastName === "") {
      setNewPlayerError("Enter both a first and a last name.");
      return;
    }
    if (!currentLeague) {
      setNewPlayerError("No league selected.");
      return;
    }

    setNewPlayerError(null);
    setIsCreatingPlayer(true);

    apiPost<Player>(
      getPlayersUrl(currentLeague.id),
      { player: { first_name: firstName, last_name: lastName } },
      {
        token: auth.token,
        ...(currentLeague.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
      },
    )
      .then((created) => {
        setIsCreatingPlayer(false);
        setIsAddingPlayer(false);
        setNewFirstName("");
        setNewLastName("");
        // A player created here is new to the league, so they have no roster
        // number yet — the jersey step is where it gets recorded.
        handleSubstituteSelect(
          toPlayerOption(
            created.id,
            created.first_name,
            created.last_name,
            null,
          ),
        );
        showToast(`${firstName} ${lastName} added to the league`, "success");
      })
      .catch((error) => {
        setNewPlayerError(error.message || "Could not add the player.");
        setIsCreatingPlayer(false);
      });
  };

  const handleSave = () => {
    setErrorMessage(null);

    if (absentPlayerId === null || teamId === null) {
      setErrorMessage("Choose who is missing.");
      setActiveStepKey("absent");
      return;
    }

    setIsSubmitting(true);

    // Both optional fields are sent explicitly, including as null, so that
    // clearing a substitute or a jersey on an existing spare takes effect.
    const requestBody = {
      spare: {
        team_id: teamId,
        replaced_player_id: absentPlayerId,
        spare_player_id: substitute?.id ?? null,
        jersey: jersey.trim() || null,
      },
    };

    const url = editingSpare
      ? getSpareUrl(editingSpare.id)
      : getSparesUrl(game.id);
    const apiFn = editingSpare ? apiPatch : apiPost;

    apiFn(url, requestBody, {
      token: auth.token,
      ...(currentLeague?.abbr ? { leagueAbbr: currentLeague.abbr } : {}),
    })
      .then(() => {
        showToast(editingSpare ? "Spare updated" : "Spare added", "success");
        onSaved();
      })
      .catch((error) => {
        setErrorMessage(error.message || "An error occurred");
        setIsSubmitting(false);
      });
  };

  const renderAddPlayerForm = () =>
    isAddingPlayer ? (
      <div className="ee-new-player">
        <div className="ee-new-player-fields">
          <input
            className="ee-search"
            type="text"
            aria-label="New player first name"
            placeholder="First name"
            value={newFirstName}
            autoFocus
            onChange={(event) => setNewFirstName(event.target.value)}
          />
          <input
            className="ee-search"
            type="text"
            aria-label="New player last name"
            placeholder="Last name"
            value={newLastName}
            onChange={(event) => setNewLastName(event.target.value)}
          />
        </div>
        {newPlayerError && <p className="ee-error">{newPlayerError}</p>}
        <div className="ee-option-grid">
          <button
            type="button"
            className="ee-option-btn"
            disabled={isCreatingPlayer}
            onClick={handleCreatePlayer}
          >
            <span className="ee-option-label">
              {isCreatingPlayer ? "Adding..." : "Add and use"}
            </span>
          </button>
          <button
            type="button"
            className="ee-option-btn"
            disabled={isCreatingPlayer}
            onClick={() => {
              setIsAddingPlayer(false);
              setNewPlayerError(null);
            }}
          >
            <span className="ee-option-label">Cancel</span>
          </button>
        </div>
      </div>
    ) : (
      <div className="ee-option-grid ee-option-grid--lead">
        <button
          type="button"
          className="ee-option-btn"
          onClick={() => {
            // Seed the name from whatever was typed into the search, so a
            // fruitless search flows straight into adding that player.
            const typed = search.searchTerm.trim();
            const [first, ...rest] = typed.split(/\s+/);
            setNewFirstName(rest.length > 0 ? first : typed);
            setNewLastName(rest.join(" "));
            setNewPlayerError(null);
            setIsAddingPlayer(true);
          }}
        >
          <span className="ee-option-label">+ Add a new player</span>
        </button>
      </div>
    );

  const renderSubstituteStep = () => (
    <>
      <input
        className="ee-search"
        type="text"
        inputMode="search"
        placeholder="Search by first or last name..."
        aria-label="Search league players"
        value={search.searchTerm}
        onChange={(event) => search.setSearchTerm(event.target.value)}
      />
      <span className="ee-search-status">
        {search.isLoading
          ? "Searching..."
          : search.error
            ? search.error
            : `${searchResults.length} player${
                searchResults.length === 1 ? "" : "s"
              }${
                searchResults.length === PLAYER_SEARCH_LIMIT
                  ? " — type to narrow the list"
                  : ""
              }`}
      </span>
      {renderAddPlayerForm()}
      <PlayerGrid
        rosters={[
          { teamId: 0, teamName: "League players", players: searchResults },
        ]}
        selectedPlayerId={substitute?.id ?? null}
        onSelect={(playerId) =>
          handleSubstituteSelect(
            searchResults.find((player) => player.id === playerId) ?? null,
          )
        }
        excludedPlayerIds={absentPlayerId === null ? [] : [absentPlayerId]}
        noneLabel="No substitute"
        noneSelected={substituteAnswered && substitute === null}
        onSelectNone={() => handleSubstituteSelect(null)}
        showTeamNames={false}
        emptyMessage={
          search.searchTerm.trim() === ""
            ? "No players in this league yet."
            : `No players found matching "${search.searchTerm}".`
        }
      />
    </>
  );

  const renderJerseyStep = () => (
    <>
      <div className="ee-jersey-field">
        <input
          className="ee-jersey-input"
          type="text"
          inputMode="numeric"
          maxLength={3}
          aria-label="Jersey number"
          placeholder={substitute?.jerseyLabel ?? "17"}
          value={jersey}
          onChange={(event) => setJersey(event.target.value)}
        />
        <span className="ee-clock-caption">Jersey</span>
      </div>
      {jersey !== "" && (
        <div className="ee-option-grid ee-option-grid--lead">
          <button
            type="button"
            className="ee-option-btn"
            onClick={() => setJersey("")}
          >
            <span className="ee-option-label">Use their own number</span>
          </button>
        </div>
      )}
      <p className="ee-hint">
        Leave this blank unless the substitute wore a different number for this
        game.
      </p>
    </>
  );

  const steps: EntryStep[] = [
    {
      key: "absent",
      chipLabel: "Absent",
      chipValue: absentPlayer
        ? `#${absentPlayer.jerseyLabel} ${absentPlayer.name}`
        : absentPlayerId === null
          ? null
          : `Player ${absentPlayerId}`,
      prompt: "Who is missing?",
      render: () => (
        <PlayerGrid
          rosters={rosters}
          selectedPlayerId={absentPlayerId}
          onSelect={handleAbsentSelect}
          emptyMessage="Everyone on this team is already accounted for."
        />
      ),
    },
    {
      key: "substitute",
      chipLabel: "Sub",
      chipValue: !substituteAnswered
        ? null
        : substitute
          ? `#${substitute.jerseyLabel} ${substitute.name}`
          : "None",
      prompt: "Who subbed in?",
      render: renderSubstituteStep,
    },
  ];

  // Without a substitute there is no number to record, so the step is dropped
  // rather than shown as an unanswerable chip.
  if (substitute !== null) {
    steps.push({
      key: "jersey",
      chipLabel: "Jersey",
      chipValue: jersey || substitute.jerseyLabel || "Default",
      prompt: "What number did they wear?",
      render: renderJerseyStep,
    });
  }

  return (
    <EntryPanel
      title={editingSpare ? "Edit spare" : "Add spare"}
      subtitle={subtitle}
      steps={steps}
      activeStepKey={activeStepKey}
      onStepSelect={setActiveStepKey}
      errorMessage={errorMessage}
      isSubmitting={isSubmitting}
      canSave={absentPlayerId !== null}
      saveLabel="Save spare"
      onSave={handleSave}
      onCancel={onCancel}
    />
  );
}

export default SpareEntry;
